import { ProductSliderWidget } from '@migros/msrc-articles';
import * as React from 'react';

import { isNotEmptyArray } from '@impuls-coach/util-is';
import { CardRenderLocationType } from 'containers/molecules/Card/enum-card-render-location';
import i18n from 'helper/i18n';
import { RecipeCalculatorBoundariesType } from 'store/recipes';
import { ContainType, DurationType, HintType, RecipeType, StepType } from 'types/Recipe';
import { ProductSliderWidgetConfig as ProductSliderWidgetConfigWithoutIds } from '../../../defs/msrc';

import Icon from 'components/atoms/Icon';
import IconInfo from 'components/atoms/IconInfo';
import Bar from 'components/molecules/Bar';
import BarItem from 'components/molecules/BarItem';
import ButtonBar, { ButtonBarProps } from 'components/molecules/ButtonBar';
import RecipeIngredientList from 'components/molecules/Recipe/fragments/IngredientList';
import Rich from 'components/molecules/Rich';
import CARD_RENDER_LOCATION from 'containers/molecules/Card/enum-card-render-location';
import RecipeCalculator from 'containers/molecules/Recipe/fragments/Calculator';
import { UiStateTypes } from '../../hoc/Cards/withUiState';

export type RecipePropsType = {
	id: string;
	externalId: string;
	recipe?: RecipeType & {
		variants: {
			[index: number]: {
				base_size: {
					text: string;
				};
			};
		};
	};
	isMigustoRecipe: boolean;
	isDone: boolean;
	isCheckable: boolean;
	isUpdating: boolean;
	isDisabled: boolean;
	selectedContent: any;
	shouldShowSwapButton: boolean;
	handleButtonClicks: (iconOrId: string, ev: React.MouseEvent<any>) => void;
	selectedPortion: number;
	variantList: number[];
	hasStaticSelectedContent?: boolean;
	cardRenderLocation: CardRenderLocationType;
	directionBoundaries: RecipeCalculatorBoundariesType;
	onClickClose: () => {};
	isMigrosAuthenticated: boolean;
	productsIds: string;
	uiState: UiStateTypes;
};

const labelMigustoText = () => {
	const text = 'react.impulsCoach.components.recipe.migusto.text';
	const linkLabel = i18n('react.impulsCoach.components.recipe.migusto.linkLabel');
	const linkHref = i18n('react.impulsCoach.components.recipe.migusto.linkHref');
	return i18n(text, {
		link: `<a href="${linkHref}" target="_blank">${linkLabel}</a>`,
		__disableHtmlEncoding: true,
	});
};

export class Recipe extends React.Component<RecipePropsType> {
	public static displayName = 'Recipe';

	public renderButtonBar = () => {
		const {
			isDone,
			isCheckable,
			handleButtonClicks,
			isUpdating,
			isDisabled,
			shouldShowSwapButton,
			cardRenderLocation = CARD_RENDER_LOCATION.PROGRAM_FEED,
		} = this.props;
		let cardButtons: null | Partial<ButtonBarProps> = null;

		if (cardRenderLocation === CARD_RENDER_LOCATION.PROGRAM_FEED && !isDone) {
			cardButtons = {
				buttons: [
					{
						disabled: !isCheckable || isUpdating,
						knob: {
							modifier: 'dashed',
							size: 'l',
							icon: 'completed',
						},
					},
				],
			} as ButtonBarProps;
		}

		if (cardRenderLocation === CARD_RENDER_LOCATION.POOL && shouldShowSwapButton) {
			cardButtons = {
				buttons: [
					{
						id: 'select-recipe',
						link: {
							modifier: 'button',
							iconRight: '',
							iconLeft: '',
							title: i18n('react.impulsCoach.components.cardDetail.recipe.selectReplaceText'),
							buttonBarId: 'select',
						},
					},
				],
			} as ButtonBarProps;
		}

		if (cardButtons === null) {
			return null;
		}

		return (
			<div className="m-card__button-bar m-card__button-bar--inline">
				<ButtonBar
					handleButtonClicks={handleButtonClicks}
					disabled={isDisabled}
					{...(cardButtons as ButtonBarProps)}
				/>
			</div>
		);
	};

	public handleCloseRecipe = () => {
		this.props.onClickClose();
	};

	public renderRecipeIngredientList() {
		const { recipe, cardRenderLocation, selectedPortion, isMigrosAuthenticated, productsIds, uiState } = this.props;
		const selectedRecipe = recipe!;
		if (selectedRecipe.ingredients && isNotEmptyArray(selectedRecipe.ingredients)) {
			return Object.keys(selectedRecipe.variants).map((variantAmount) => {
				return (
					<RecipeIngredientList
						key={parseFloat(variantAmount)}
						cardRenderLocation={cardRenderLocation as any}
						variantAmount={parseFloat(variantAmount)}
						selectedPortion={selectedPortion}
						ingredients={selectedRecipe.variants[variantAmount].ingredients}
						handleCloseRecipe={this.handleCloseRecipe}
						isMigrosAuthenticated={isMigrosAuthenticated}
						productsIds={productsIds}
						uiState={uiState}
					/>
				);
			});
		}
		return null;
	}

	public render() {
		const { recipe = {} as any, isMigustoRecipe, variantList } = this.props;

		const selectedRecipe = this.props.recipe!;

		const productSliderWidgetConfig = {
			...ProductSliderWidgetConfigWithoutIds,
			filterOptions: {
				...ProductSliderWidgetConfigWithoutIds.filterOptions,
				ids: this.props.productsIds,
			},
		};

		const text = {
			nutrients: {
				title: i18n('react.impulsCoach.components.recipe.nutrients.title'),
				protein: i18n('react.impulsCoach.components.recipe.nutrients.protein'),
				fat: i18n('react.impulsCoach.components.recipe.nutrients.fat'),
				carbohydrates: i18n('react.impulsCoach.components.recipe.nutrients.carbohydrates'),
			},
			ingredients: {
				title: i18n('react.impulsCoach.components.recipe.ingredients.title'),
			},
			howto: {
				title: i18n('react.impulsCoach.components.recipe.howto.title'),
				subTitleDuration: i18n('react.impulsCoach.components.recipe.howto.subTitleDuration'),
				subTitleSteps: i18n('react.impulsCoach.components.recipe.howto.subTitleSteps'),
				goodToKnow: i18n('react.impulsCoach.components.recipe.howto.goodToKnow'),
			},
			intolerances: {
				title: i18n('react.impulsCoach.components.recipe.intolerances.title'),
				noIntolerances: i18n('react.impulsCoach.components.recipe.intolerances.noIntolerances'),
			},
		};

		return (
			<div className="m-recipe">
				<Bar>
					<BarItem>
						<IconInfo
							icon="weight-balance"
							value={
								selectedRecipe.nutrients && selectedRecipe.nutrients.calories
									? selectedRecipe.nutrients.calories.toString()
									: '0'
							}
							unit="kcal"
						/>
					</BarItem>
					<BarItem>
						<IconInfo icon="time" value={selectedRecipe.totalDurationFormatted} />
					</BarItem>
					{selectedRecipe.nutrition && (
						<BarItem>
							<IconInfo
								icon={`nutrition-${selectedRecipe.nutrition}`}
								value={i18n(
									`react.impulsCoach.components.tagFilter.recipe.RECIPE_DIET_FORM.${selectedRecipe.nutrition.toUpperCase()}`
								)}
							/>
						</BarItem>
					)}
				</Bar>
				<div className="m-recipe__block">
					<h3 className="m-recipe__sub-title">{text.nutrients.title}</h3>
					<ul className="m-recipe__nutrients">
						<li className="m-recipe__nutrient">
							<span className="m-recipe__nutrient-bubble">
								{selectedRecipe.nutrients && selectedRecipe.nutrients.proteins}g
							</span>
							<span className="m-recipe__nutrient-text">{text.nutrients.protein}</span>
						</li>
						<li className="m-recipe__nutrient">
							<span className="m-recipe__nutrient-bubble">
								{selectedRecipe.nutrients && selectedRecipe.nutrients.fat}g
							</span>
							<span className="m-recipe__nutrient-text">{text.nutrients.fat}</span>
						</li>
						<li className="m-recipe__nutrient">
							<span className="m-recipe__nutrient-bubble">
								{selectedRecipe.nutrients && selectedRecipe.nutrients.carbohydrates}g
							</span>
							<span className="m-recipe__nutrient-text">{text.nutrients.carbohydrates}</span>
						</li>
					</ul>
				</div>
				<div className="m-recipe__block">
					<h3 className="m-recipe__title">
						{text.ingredients.title}{' '}
						{variantList.length < 2 && recipe.baseSize ? recipe.baseSize.text : '' || ''}
					</h3>
					{this.props.externalId && (
						<RecipeCalculator
							key={`${this.props.id}-${this.props.externalId}`}
							id={this.props.id}
							externalId={this.props.externalId}
							selectedContent={this.props.selectedContent}
						/>
					)}
					{this.renderRecipeIngredientList()}
				</div>
				{this.props.productsIds !== '' && this.props.isMigrosAuthenticated && (
					<div className="m-recipe__block">
						<h3 className="m-recipe__title">
							{i18n('react.impulsCoach.components.recipe.products.title')}
						</h3>
						<div className="m-recipe__wide">
							<div className="m-recipe__wide-products">
								<ProductSliderWidget {...productSliderWidgetConfig} />
							</div>
						</div>
					</div>
				)}
				<div className="m-recipe__block">
					<h3 className="m-recipe__title">{text.howto.title}</h3>
					<h4 className="m-recipe__sub-title">{text.howto.subTitleDuration}</h4>
					{selectedRecipe.duration && isNotEmptyArray(selectedRecipe.duration)
						? selectedRecipe.duration.map((duration: DurationType, index: number) => {
								return (
									<span key={index}>
										{`${index !== 0 ? '+ ' : ''} ${duration.prefix} ${duration.quantity} ${
											duration.unit
										} ${duration.text}`}
										<br />
									</span>
								);
						  })
						: null}
				</div>

				{selectedRecipe.steps && isNotEmptyArray(selectedRecipe.steps) ? (
					<div>
						<h4 className="m-recipe__sub-title">{text.howto.subTitleSteps}</h4>
						<ol className="m-recipe__steps">
							{selectedRecipe.steps.map((step: StepType, index: number) => {
								return (
									<li key={index}>
										{step.title ? (
											<span>
												{step.title} <br />
											</span>
										) : (
											''
										)}
										{step.description}
									</li>
								);
							})}
						</ol>
					</div>
				) : null}

				{selectedRecipe.hints && isNotEmptyArray(selectedRecipe.hints) ? (
					<div>
						<h4 className="m-recipe__hints-title">
							<span className="m-recipe__hints-icon">
								<Icon iconName="tip" />
							</span>
							{text.howto.goodToKnow}
						</h4>
						<ul className="m-recipe__hints">
							{selectedRecipe.hints.map((hint: HintType, index: number) => {
								return (
									<li key={index} className="m-recipe__hint">
										{hint.text}
									</li>
								);
							})}
						</ul>
					</div>
				) : null}

				{this.renderButtonBar()}

				{selectedRecipe.contains && isNotEmptyArray(selectedRecipe.contains) ? (
					<div>
						<h3 className="m-recipe__title m-recipe__title--info">{text.intolerances.title}</h3>
						<ul className="m-recipe__intolerances">
							{selectedRecipe.contains.map((contain: ContainType, index: number) => {
								return (
									<li key={index} className="m-recipe__intolerance">
										<span className="m-recipe__intolerance-icon">
											{contain.iconName && (
												<span className="m-recipe__intolerance-bubble">
													<Icon iconName={contain.iconName} />
												</span>
											)}
										</span>
										<span className="m-recipe__intolerance-text">{contain.name}</span>
									</li>
								);
							})}
						</ul>
					</div>
				) : (
					<Rich contents={text.intolerances.noIntolerances} />
				)}

				{isMigustoRecipe && <Rich contents={labelMigustoText()} />}
			</div>
		);
	}
}

export default Recipe;
